import { Grid, Stack } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import SelectDateModal from '@components/SelectDateModal/SelectDateModal';
import { ApplicationStatus, DatePeriod } from '@features/applications';
import AddApplicantManually from '@features/applications/components/AddApplicantManually/AddApplicantManually';
import ApplicationsCountsList from '@features/applications/components/ApplicationsCountsList/ApplicationsCountsList';
import ApplicationsList from '@features/applications/components/ApplicationsList/ApplicationsList';
import ApplicationsListFilter from '@features/applications/components/ApplicationsListFilters/ApplicationsListFilters';
import { useGetEmployerApplications } from '@features/applications/hooks/useGetEmployerApplications';
import { useSearchApplications } from '@features/applications/hooks/useSearchAppliations';
import BoostButton from '@features/boostMyJobs/components/BoostButton/BoostButton';
import EmployerMetricsHeader from '@features/employerMetrics/components/EmployerMetricsHeader/EmployerMetricsHeader';
import EmployerOnboardingModal from '@features/employerOnboarding/components/EmployerOnboardingModal/EmployerOnboardingModal';
import EmployerHireflixOnboardingModal from '@features/employerOnboarding/components/EmployerHireflixOnboardingModal/EmployerHireflixOnboardingModal';
import Notifications from '@features/notifications/components/Notifications/Notifications';
import {
  authContext,
  Position,
  Profile,
  useGetJobPostings,
} from '@staffedupusa/shared-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetCurrentPlan } from '@features/payments/hooks/useGetCurrentPlan';
import { setUserPropertiesToAmplitude } from '@utils/amplitudeUserProperties';
import BetterHiringChecklist from '@features/employerOnboarding/components/BetterHiringChecklist/BetterHiringChecklist';
import { getCookie } from '@utils/getCookie';
import ActiveJobsModal from '@features/employerOnboarding/components/ActiveJobsModal/ActiveJobsModal';
import CompanyLogoAndCover from '@features/company/components/CompanyLogoAndCover/CompanyLogoAndCover';
import CongratsOnHire from '@features/employerOnboarding/components/CongratsOnHire/CongratsOnHire';
import AskMoreLocations from '@features/company/components/AskMoreLocations/AskMoreLocations';
import StaffLevelWarning from '@features/employerTeam/components/StaffLevelWarning';
import { white } from '@styles/colors';
import SatffedFullModal from '@features/employerTeam/components/SatffedFullModal';
import { JobPosition } from '@features/hireflixJob';
import { useGetJobPositions } from '@features/hireflixJob/hooks/useGetJobPositions';

dayjs.extend(isSameOrAfter);

const EmployerDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { authData } = useContext(authContext);
  const [showJobActivatedModal, setShowJobActivatedModal] = useState(false);
  const [status, setStatus] = useState<ApplicationStatus | string>('All');
  const [isWOTCOnly, setIsWOTCOnly] = useState<boolean>(false);
  const [positions, setPositions] = useState<string[]>([]);
  const [rating, setRating] = useState<'All' | number>('All');
  const [searchValue, setSearchValue] = useState<string>('');
  const [applicationsListAlertText, setApplicationsListAlertText] =
    useState<string>('');
  const [selectedDatePeriod, setSelectedDatePeriod] =
    useState<DatePeriod>('ALL');
  const [date, setDate] = useState<Dayjs | string | null>(null);
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] =
    useState<boolean>(false);
  const [isFilterSelected, setIsFilterSelected] = useState<boolean>(false);
  const [selectedTag, setSelectedTag] = useState('All');
  const [selectedInterviewType, setSelectedInterviewType] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [showBetterHiringChecklist, setShowBetterHiringChecklist] =
    useState(false);
  const [triggerChange, setTriggerChange] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const preSelectedTag = queryParams.get('selected-tag');
  const betterHiring = queryParams.get('better-hiring');
  const inboxChecked = getCookie('inboxChecked');
  const isCheckedLocationQuestion =
    authData?.user?.company?.check_list?.has_more_locations_answered;

  const isCheckedHireflixOnboarding =
    authData?.user?.company?.check_list?.hireflix_onboarding;
  const isWalkThroughCompleted = authData.user?.walkthrough_completed;

  const testingCompanyId = Number(
    import.meta.env.VITE_HIREFLIX_TESTING_COMPANY,
  );
  const isHireflixTestingCompany =
    authData?.user?.company?.id === testingCompanyId;

  const { currentPlanData } = useGetCurrentPlan();
  const { jobPostingsData, isLoadingJobPostings } = useGetJobPostings();
  const { getApplications, applicationsData, isLoadingApplications } =
    useGetEmployerApplications({
      status,
      selectedInterviewType,
      pageNo,
      date,
      isWOTCOnly,
      rating,
      positions,
      selectedTag,
      enabled: searchValue.length < 3,
      setAlertText: setApplicationsListAlertText,
    });

  const { searchApplicationsData, isSearchingApplications } =
    useSearchApplications({
      searchValue: searchValue.length > 2 ? searchValue : '',
      setAlertText: setApplicationsListAlertText,
    });

  const [showHireflixModal, setShowHireflixModal] = useState(false);
  const [localJobPostings, setLocalJobPostings] = useState<Position[] | []>([]);
  const { jobPositionsData, isLoadingJobPositions } = useGetJobPositions({
    created_list: true,
  });

  useEffect(() => {
    if (!jobPositionsData || isLoadingJobPositions) return;

    const hasSeenModalThisSession = sessionStorage.getItem('hireflixModalSeen');

    if (
      jobPositionsData?.created_job_list?.length === 0 &&
      isHireflixTestingCompany &&
      isWalkThroughCompleted &&
      !isCheckedHireflixOnboarding &&
      !hasSeenModalThisSession
    ) {
      setShowHireflixModal(true);
      sessionStorage.setItem('hireflixModalSeen', 'true');
    }
  }, [
    jobPositionsData,
    isHireflixTestingCompany,
    isWalkThroughCompleted,
    isCheckedHireflixOnboarding,
    isLoadingJobPositions,
  ]);

  useEffect(() => {
    if (jobPostingsData?.object) {
      setLocalJobPostings(jobPostingsData.object);
    }
  }, [isLoadingJobPostings, jobPostingsData]);
  const getApplicationsList = () => {
    if (searchApplicationsData?.object) {
      return searchApplicationsData?.object;
    }
    if (applicationsData?.object) {
      return applicationsData.object;
    }
    return [];
  };

  const getAllApplicationsIds = () => {
    if (applicationsData?.application_ids) {
      return applicationsData.application_ids;
    }
  };

  const getApplicationsCount = () => {
    if (searchApplicationsData?.object) {
      return searchApplicationsData?.object?.length ?? 0;
    }
    if (applicationsData?.total_applications) {
      return applicationsData.total_applications;
    }
    return 0;
  };

  const getDateForFiltration = () => {
    let dateToSet: Dayjs | string | null = dayjs();

    switch (selectedDatePeriod) {
      case 'ALL':
        dateToSet = null;
        break;
      case 'TODAY':
        dateToSet = 'TODAY';
        break;
      case 'YESTERDAY':
        dateToSet = 'YESTERDAY';
        break;
      case 'WEEK':
        dateToSet = 'WEEK';
        break;
      case 'MONTH':
        dateToSet = 'MONTH';
        break;
      case 'custom date':
        dateToSet = null;
        setIsCustomDateModalOpen(true);
        break;
      default:
        break;
    }

    setDate(dateToSet);
  };

  const handleTagsChange = (event: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setSelectedTag(event.target.value);
  };

  const handleSelectedInterviewTypeChange = (type: string) => {
    setSelectedInterviewType(type);
  };
  const handlePageNo = (newPageNo: number) => {
    setPageNo(newPageNo);
  };
  const fullJobs = localJobPostings?.filter(
    (item) => item.staff_count.is_full === 2,
  );
  const totalStatff = localJobPostings
    .filter((item) => item.status !== 4)
    .reduce(
      (acc, pos) => {
        acc.totalCurrent += Number(pos.staff_count.current_staff_count) || 0;
        acc.totalDesired += Number(pos.staff_count.desired_staff_count) || 0;
        return acc;
      },
      { totalCurrent: 0, totalDesired: 0 },
    );
  useEffect(() => getDateForFiltration(), [selectedDatePeriod]);

  useEffect(() => {
    getApplications();
  }, [status, isWOTCOnly, positions, rating, selectedTag]);

  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true);
      return;
    }
    setTriggerChange(true);
    setPageNo(1);
    if (
      status !== 'All' ||
      date ||
      isWOTCOnly ||
      rating !== 'All' ||
      selectedTag !== 'All' ||
      positions.length > 0
    ) {
      setIsFilterSelected(true);
    } else {
      setIsFilterSelected(false);
    }
  }, [status, date, isWOTCOnly, positions, rating, selectedTag]);

  useEffect(() => {
    preSelectedTag ? setSelectedTag(preSelectedTag) : null;
  }, []);

  useEffect(() => {
    if (authData?.user && authData?.user?.company) {
      setUserPropertiesToAmplitude(
        authData.user as Profile & {
          sub_users_count: number;
          enterprise_companies_count: number;
        },
        currentPlanData,
      );
    }
    if (
      authData?.user &&
      authData?.user?.walkthrough_completed &&
      !state?.dontShow &&
      (authData?.user?.walkthrough_status < 1 || betterHiring)
    ) {
      if (authData?.user?.company?.plan === 4) {
        if (
          !authData?.user?.has_shared_page ||
          !authData?.user?.has_image ||
          !inboxChecked ||
          !authData?.user?.company?.check_list
            ?.has_visited_qualification_question_page ||
          !authData?.user?.company?.check_list?.has_visited_job_posting_page
        ) {
          setShowBetterHiringChecklist(true);
        }
      } else if (authData?.user?.company?.plan === 1) {
        if (
          !authData?.user?.has_shared_page ||
          !authData?.user?.has_image ||
          !authData?.user?.company?.check_list
            ?.has_visited_qualification_question_page ||
          !authData?.user?.company?.check_list?.has_visited_job_posting_page ||
          ((!authData?.user?.company?.check_list
            ?.has_added_pre_qualification_questions ||
            !authData?.user?.calendar_count ||
            !authData?.user?.company?.auto_save_applications ||
            !authData?.user?.company?.auto_interview_invite) &&
            !authData?.user?.has_facebook_page_id)
        ) {
          setShowBetterHiringChecklist(true);
        }
      } else if (authData?.user?.company?.plan === 2) {
        if (
          !authData?.user?.has_shared_page ||
          !authData?.user?.has_image ||
          !authData?.user?.company?.check_list
            ?.has_visited_qualification_question_page ||
          !authData?.user?.company?.check_list?.has_visited_job_posting_page ||
          ((!authData?.user?.company?.check_list
            ?.has_added_pre_qualification_questions ||
            !authData?.user?.calendar_count ||
            !authData?.user?.company?.auto_save_applications ||
            !authData?.user?.company?.auto_interview_invite) &&
            !authData?.user?.has_facebook_page_id) ||
          (!authData?.user?.package_template &&
            !authData?.user?.has_mission_statement)
        ) {
          setShowBetterHiringChecklist(true);
        }
      }
    }
  }, [authData, betterHiring]);

  useEffect(() => {
    if (
      authData?.user &&
      authData?.user?.company &&
      authData?.user?.company?.plan === 4 &&
      jobPostingsData &&
      authData.user?.walkthrough_completed &&
      jobPostingsData?.object?.filter(
        (position: Position) => position.status !== 4,
      ).length > 1
    ) {
      setShowJobActivatedModal(true);
    }
  }, [authData, jobPostingsData]);
  return (
    <>
      <Helmet>
        <title>Dashboard | StaffedUp</title>
      </Helmet>
      <Stack spacing={5} pt={5} pb={4} px={3} pl={6}>
        <Notifications />
        <Grid container sx={{ marginTop: '0 !important' }}>
          <Grid
            item
            xs={12}
            md={3}
            pr={1}
            pb={3}
            mb={6}
            sx={{ paddingTop: '0 !important' }}
          >
            {!isCheckedLocationQuestion && <AskMoreLocations />}
            <CompanyLogoAndCover />
            <Grid
              sx={{ margin: '10px 0px', background: white }}
              item
              p={2}
              borderRadius="8px"
              width={{ sm: '100%', mg: 250 }}
              container
            >
              <Grid item sm={12} lg={6} sx={{ padding: '0 5px' }}>
                <BoostButton />
              </Grid>
              <Grid
                item
                sm={12}
                lg={6}
                sx={{ padding: '0 5px', marginTop: { md: '10px', lg: '0' } }}
              >
                <AddApplicantManually
                  setTriggerChange={setTriggerChange}
                  handlePageNo={handlePageNo}
                />
              </Grid>
            </Grid>
            <Stack spacing={2} mt={3} mb={2}>
              <StaffLevelWarning
                percent={
                  (totalStatff.totalCurrent / totalStatff.totalDesired) * 100
                }
                localJobPostings={localJobPostings.filter(
                  (item) => item.status !== 4,
                )}
              />
              <ApplicationsCountsList
                selectedPositions={positions}
                setSelectedPositions={setPositions}
                disableButtons={searchValue.length > 2}
                handlePageNo={handlePageNo}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={9} pl={1} sx={{ paddingTop: '0 !important' }}>
            {/* <EmployerMetricsHeader /> */}
            <ApplicationsListFilter
              selectedStatus={status}
              setSelectedStatus={setStatus}
              rating={rating}
              setRating={setRating}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              disableButtons={searchValue.length > 2}
              selectedDatePeriod={selectedDatePeriod}
              setSelectedDatePeriod={setSelectedDatePeriod}
              openCustomDateModal={() => setIsCustomDateModalOpen(true)}
              handlePageNo={handlePageNo}
              isWOTCOnly={isWOTCOnly}
              setIsWOTCOnly={setIsWOTCOnly}
              handleTagsChange={handleTagsChange}
              selectedTag={selectedTag}
              handleSelectedInterviewTypeChange={
                handleSelectedInterviewTypeChange
              }
              selectedInterviewType={selectedInterviewType}
            />
            <ApplicationsList
              handlePageNo={handlePageNo}
              triggerChange={triggerChange}
              setTriggerChange={setTriggerChange}
              pageNo={pageNo}
              applications={getApplicationsList()}
              isLoading={isLoadingApplications || isSearchingApplications}
              isWOTCOnly={isWOTCOnly}
              setIsWOTCOnly={setIsWOTCOnly}
              searchValue={searchValue}
              alertText={applicationsListAlertText}
              disableButtons={searchValue.length > 2}
              handleTagsChange={handleTagsChange}
              selectedTag={selectedTag}
              totalAplications={getApplicationsCount()}
              allApplicationsIds={getAllApplicationsIds()}
              selectedStatus={status}
              setSearchValue={setSearchValue}
              isFilterSelected={isFilterSelected}
            />
          </Grid>
        </Grid>
      </Stack>

      {!authData.user?.walkthrough_completed && <EmployerOnboardingModal />}

      {showHireflixModal && (
        <EmployerHireflixOnboardingModal
          setShowHireflixModal={setShowHireflixModal}
        />
      )}

      <SelectDateModal
        isOpen={isCustomDateModalOpen}
        handleClose={() => setIsCustomDateModalOpen(false)}
        date={date}
        setDate={setDate}
      />
      {authData?.user?.company?.plan !== 2 && <CongratsOnHire />}
      {showBetterHiringChecklist && (
        <BetterHiringChecklist
          open={showBetterHiringChecklist}
          handleClose={() => {
            setShowBetterHiringChecklist(false);
            navigate('/dashboard/', {
              state: betterHiring ? { dontShow: true } : {},
            });
          }}
        />
      )}
      {showJobActivatedModal &&
        jobPostingsData &&
        jobPostingsData?.object?.length > 0 && (
          <ActiveJobsModal
            open={showJobActivatedModal}
            handleClose={() => {
              setShowJobActivatedModal(false);
            }}
            positions={jobPostingsData?.object}
          />
        )}
      {fullJobs.length > 0 && <SatffedFullModal fullJobs={fullJobs} />}
    </>
  );
};

export default EmployerDashboard;
